import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Check } from "lucide-react";
import { Countdown } from "./Countdown";
import { LiveBadge } from "./LiveBadge";
import { CountUp } from "./CountUp";
import { formatKickoff, isLocked, lockTime } from "@/lib/time";
import { calcPoints } from "@/lib/scoring";
import { teamDe } from "@/lib/teamNames";

type Game = {
  id: string; game_no: number; group_label: string; phase?: string;
  home_team: string; away_team: string;
  home_flag: string; away_flag: string;
  venue: string | null;
  kickoff_time: string;
  home_score: number | null; away_score: number | null;
  is_finished: boolean;
  is_live?: boolean;
  minute?: number | null;
  status_short?: string | null;
  live_home_score?: number | null;
  live_away_score?: number | null;
};
type Tip = { home_tip: number; away_tip: number; points_earned: number };

export function GameCard({ game, tip }: { game: Game; tip?: Tip }) {
  const locked = isLocked(game.kickoff_time);
  const finished = game.is_finished && game.home_score != null;
  const live = !!game.is_live && !finished;
  const dispHome = live ? (game.live_home_score ?? 0) : (game.home_score ?? 0);
  const dispAway = live ? (game.live_away_score ?? 0) : (game.away_score ?? 0);
  const livePoints = live && tip ? calcPoints(dispHome, dispAway, tip.home_tip, tip.away_tip) : 0;

  const status = live
    ? null // LiveBadge takes over
    : finished
    ? { text: "Beendet", cls: "bg-muted text-muted-foreground" }
    : locked
    ? { text: "Tipps gesperrt", cls: "bg-primary text-primary-foreground" }
    : tip
    ? { text: "Getippt ✓", cls: "bg-accent text-accent-foreground" }
    : { text: "Offen", cls: "bg-secondary text-secondary-foreground" };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      className={`bg-card rounded-lg shadow-card overflow-hidden ${live ? "ring-2 ring-primary/40" : ""}`}
    >
      <Link to={`/spiel/${game.id}`} className="block tap">
        <div className="flex items-center justify-between px-4 pt-3">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Spiel {game.game_no} · {(!game.phase || game.phase === "group") ? `Gruppe ${game.group_label}` : (game.group_label || "KO")}
          </span>
          {live ? (
            <LiveBadge minute={game.minute} status={game.status_short} />
          ) : status && (
            <span className={`text-[10px] font-bold px-2 py-1 rounded-pill ${status.cls} flex items-center gap-1`}>
              {locked && !finished && <Lock className="h-3 w-3" />}
              {tip && !locked && <Check className="h-3 w-3" />}
              {status.text}
            </span>
          )}
        </div>

        <div className="px-4 py-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="text-right">
            <div className="text-2xl">{game.home_flag}</div>
            <div className="text-sm font-semibold leading-tight">{teamDe(game.home_team)}</div>
          </div>
          <div className="font-display font-extrabold text-2xl tabular-nums px-2">
            {(finished || live) ? (
              <span className="flex items-center gap-1">
                <CountUp value={dispHome} />:<CountUp value={dispAway} />
              </span>
            ) : "vs"}
          </div>
          <div className="text-left">
            <div className="text-2xl">{game.away_flag}</div>
            <div className="text-sm font-semibold leading-tight">{teamDe(game.away_team)}</div>
          </div>
        </div>

        <div className="px-4 pb-3 flex items-center justify-between gap-2 border-t border-border/60 pt-2">
          <div className="text-[11px] text-muted-foreground leading-tight">
            {formatKickoff(game.kickoff_time)}
            {game.venue && <> · {game.venue}</>}
          </div>
          {!finished && !locked && !live ? (
            <Countdown target={lockTime(game.kickoff_time)} compact />
          ) : tip ? (
            <span className="text-xs font-bold text-foreground">
              Dein Tipp: <span className="text-accent-foreground bg-accent px-1.5 py-0.5 rounded-md">{tip.home_tip}:{tip.away_tip}</span>
              {finished && (
                <span className="ml-2">
                  {tip.points_earned > 0 ? `+${tip.points_earned} 🏆` : "0 Pkt"}
                </span>
              )}
              {live && livePoints > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-md text-[10px] bg-gold text-foreground animate-pulse">
                  +{livePoints} ✨ live
                </span>
              )}
            </span>
          ) : (
            <span className="text-xs italic text-muted-foreground">Kein Tipp abgegeben</span>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
